import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, getToken, API } from "@/lib/api";
import { ProShell } from "@/components/ProShell";
import { StatusBadge } from "@/components/StatusBadge";
import { EvidenceCapture } from "@/components/EvidenceCapture";
import { SafetyReport } from "@/components/SafetyReport";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { Timeline } from "@/components/Timeline";
import { ProMaterials } from "@/components/MaterialsPanel";
import { ProReleaseJob } from "@/components/ProReleaseJob";
import { BookingChat } from "@/components/BookingChat";
import { ChevronLeft, Loader2, Check, X, Camera, MapPin, Phone, FileText, ShieldCheck, Trash2 } from "lucide-react";

const NEXT = {
    CONFIRMED: { status: "ON THE WAY", label: "START — ON THE WAY" },
    "ON THE WAY": { status: "ARRIVED", label: "MARK ARRIVED" },
    ARRIVED: { status: "INSPECTION", label: "START INSPECTION" },
    "WORK IN PROGRESS": { status: "CUSTOMER REVIEW", label: "MARK WORK DONE — SEND FOR REVIEW" },
    "REVISION REQUIRED": { status: "CUSTOMER REVIEW", label: "RE-SUBMIT FOR REVIEW" },
};

export default function ProJobDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [b, setB] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showEst, setShowEst] = useState(false);
    const [est, setEst] = useState({ visit_charge: 99, labour: 0, materials: 0, discount: 0, other: 0, note: "" });
    const [reqs, setReqs] = useState(null);
    const [capture, setCapture] = useState(null); // { kind }

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/bookings/${id}`); setB(data);
            if (data.estimate) setEst({ ...data.estimate, note: data.estimate.note || "" });
        } catch { toast.error("Could not load job"); }
        try { const { data } = await api.get(`/bookings/${id}/evidence-reqs`); setReqs(data); } catch { /* ignore */ }
    }, [id]);
    useEffect(() => { load(); }, [load]);
    useLiveRefresh(() => { if (!showEst) load(); }, 15000);

    if (!b) return <ProShell><div className="flex min-h-screen items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div></ProShell>;

    const jobAction = async (action) => {
        setBusy(true);
        try { await api.post(`/pro/jobs/${id}/${action}`); toast.success(action === "accept" ? "Job accepted" : "Job declined"); if (action === "reject") return nav("/pro"); await load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const setStatus = async (status) => {
        setBusy(true);
        try { await api.patch(`/bookings/${id}/status`, { status }); toast.success(`Updated to ${status}`); await load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const submitEstimate = async () => {
        setBusy(true);
        try {
            await api.post(`/bookings/${id}/estimate`, {
                visit_charge: lockedVisit, labour: +est.labour, materials: +est.materials,
                discount: 0, other: +est.other, note: est.note,
            });
            toast.success("Estimate submitted for customer approval");
            setShowEst(false); await load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const deleteEvidence = async (evId) => {
        try { await api.delete(`/bookings/${id}/evidence/${evId}`); toast.success("Photo removed"); await load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    const lockedVisit = +(b.estimate?.visit_charge ?? b.visit_fee ?? 0);
    const PLATFORM_FEE = 9;
    const total = lockedVisit + (+est.labour) + (+est.materials) + (+est.other) + PLATFORM_FEE;
    const vfPaid = +(b.estimate?.visit_fee_paid ?? (b.visit_fee_payment && ["Paid", "CASH_CONFIRMED"].includes(b.visit_fee_payment.status) ? b.visit_fee : 0));
    const started = !!b.work_started_at;
    const needStart = b.status === "WORK IN PROGRESS" && !started;
    const beforeItems = (b.evidence || []).filter((e) => e.kind === "before");
    const next = needStart ? null : NEXT[b.status];
    const afterShort = next?.status === "CUSTOMER REVIEW" ? Math.max(0, (reqs?.min_after ?? 1) - (b.evidence || []).filter((e) => e.kind === "after").length) : 0;
    const e0 = b.estimate || {};
    const billBlock = next?.status === "CUSTOMER REVIEW" && e0.material_source === "pro"
        ? (!e0.material_bill ? "UPLOAD MATERIAL BILL TO FINISH" : e0.material_revision?.status === "PENDING" ? "WAITING FOR CUSTOMER TO APPROVE EXTRA" : null) : null;
    const startWork = async () => {
        setBusy(true);
        try { await api.post(`/bookings/${id}/start-work`); toast.success("Work started — add After photos when done"); await load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <ProShell>
            <div className="sticky top-0 z-20 flex items-center gap-3 bg-navy px-5 pb-4 pt-5 text-white">
                <button onClick={() => nav(-1)} className="rounded-full bg-white/10 p-2 transition-colors hover:bg-white/20"><ChevronLeft size={20} /></button>
                <div><p className="text-xs text-white/50">Job</p><h1 className="font-heading text-lg font-bold">{b.booking_id}</h1></div>
                <div className="ml-auto"><StatusBadge status={b.status} /></div>
            </div>

            <div className="space-y-4 px-5 py-5">
                <Card>
                    <h3 className="font-heading font-bold text-navy">{b.service_name}</h3>
                    <p className="text-sm text-navy/60">{b.problem}</p>
                    {b.description && <p className="mt-1 rounded-lg bg-navy/[0.03] p-2 text-sm text-navy/60">{b.description}</p>}
                    <div className="mt-3 space-y-1.5 border-t border-navy/10 pt-3 text-sm">
                        <div className="flex items-center gap-2 text-navy/70"><MapPin size={15} className="text-navy/40" /> {b.address_line}, {b.area_name}</div>
                        <div className="flex items-center gap-2 text-navy/70"><Phone size={15} className="text-navy/40" /> {b.phone}</div>
                    </div>
                    {b.photos?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {b.photos.map((p, i) => <img key={i} src={`${api.defaults.baseURL}/files/${p}`} alt="" className="h-16 w-16 rounded-lg border border-navy/10 object-cover" />)}
                        </div>
                    )}
                </Card>

                {/* estimate display */}
                {b.estimate && (
                    <Card>
                        <div className="mb-2 flex items-center justify-between">
                            <h3 className="font-heading font-bold text-navy">Estimate</h3>
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${b.estimate.status === "APPROVED" ? "bg-emerald-50 text-emerald-700" : b.estimate.status === "REJECTED" ? "bg-red-50 text-red-700" : "bg-orange/15 text-orange-dark"}`}>{b.estimate.status}</span>
                        </div>
                        <div className="flex justify-between font-heading text-base font-extrabold text-navy"><span>Total</span><span>{rupee(b.estimate.total)}</span></div>
                        {b.estimate.status === "PENDING" && <p className="mt-1 text-xs text-navy/50">Waiting for customer approval.</p>}
                        {b.estimate.status === "REJECTED" && <p className="mt-1 text-xs text-red-500">Customer rejected. Do not continue paid work.</p>}
                    </Card>
                )}

                {/* service evidence (before/after) */}
                <BookingChat bookingId={b.id} role="professional" />
                <ProReleaseJob b={b} />
                <ProMaterials b={b} onChange={load} />
                {["ARRIVED", "INSPECTION", "ESTIMATE APPROVAL REQUIRED", "WORK IN PROGRESS", "CUSTOMER REVIEW", "REVISION REQUIRED"].includes(b.status) && (
                    <Card>
                        <div className="mb-1 flex items-center gap-1.5"><ShieldCheck size={16} className="text-teal" /><h3 className="font-heading font-bold text-navy">Service evidence</h3></div>
                        <p className="mb-3 text-xs text-navy/55">Step 1: capture {reqs?.min_before ?? 2}+ Before photos and tap Start Work. Step 2: after finishing, capture {reqs?.min_after ?? 1}+ After photos and send for customer review.</p>
                        {b.status === "REVISION REQUIRED" && (
                            <p data-testid="rework-note" className="mb-3 rounded-lg bg-amber-50 p-2 text-xs font-medium text-amber-700">Customer requested a rework: {[...(b.status_history || [])].reverse().find((h) => h.status === "REVISION REQUIRED")?.note || "Please redo the work."}</p>
                        )}
                        <EvidenceGroup kind="before" items={beforeItems} need={reqs?.min_before} readOnly={started || b.status === "CUSTOMER REVIEW"} onAdd={() => setCapture({ kind: "before" })} onDelete={deleteEvidence} />
                        <div className="mt-4">
                            {started
                                ? <EvidenceGroup kind="after" items={(b.evidence || []).filter((e) => e.kind === "after")} need={reqs?.min_after} readOnly={b.status === "CUSTOMER REVIEW"} onAdd={() => setCapture({ kind: "after" })} onDelete={deleteEvidence} />
                                : <p data-testid="after-locked-note" className="rounded-lg bg-navy/[0.04] p-2.5 text-xs text-navy/55">After photos unlock once you start the work.</p>}
                        </div>
                    </Card>
                )}
            </div>

            {/* safety + check-in */}
            <div className="mb-28 mt-2 space-y-2 px-1">
                {["ACCEPTED", "ON THE WAY", "PROFESSIONAL ASSIGNED", "INSPECTION", "WORK IN PROGRESS"].includes(b.status) && (
                    <button data-testid="pro-checkin-btn" onClick={async () => { try { await api.post("/safety/checkin", { booking_id: id, type: "arrival" }); toast.success("Checked in — customer notified"); } catch (e) { toast.error("Could not check in"); } }} className="flex w-full items-center justify-center gap-2 rounded-full border border-emerald-300 py-3 text-sm font-bold text-emerald-700"><MapPin size={16} /> Check in — I've arrived</button>
                )}
                <SafetyReport bookingId={id} />
            </div>

            {/* action bar */}
            <div className="mx-5 mb-44 mt-2 rounded-2xl border border-navy/10 bg-white p-4">
                <Timeline items={(b.status_history || []).map((h) => ({ ...h, label: h.note || h.status, note: h.note ? h.status : null }))} accent="#F97316" testid="pro-job-timeline" title="Job timeline" />
            </div>
            <div className="fixed bottom-16 left-0 right-0 mx-auto max-w-md border-t border-navy/10 bg-white p-4">
                {b.status === "PROFESSIONAL ASSIGNED" && (
                    <div className="flex gap-2">
                        <button data-testid="pro-accept-btn" disabled={busy} onClick={() => jobAction("accept")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white active:scale-95 disabled:opacity-60"><Check size={17} /> ACCEPT</button>
                        <button data-testid="pro-reject-btn" disabled={busy} onClick={() => jobAction("reject")} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-red-300 py-3.5 text-sm font-bold text-red-600"><X size={17} /> REJECT</button>
                    </div>
                )}
                {needStart && (
                    <button data-testid="pro-start-work-btn" disabled={busy || beforeItems.length < (reqs?.min_before ?? 2)} onClick={startWork} className="w-full rounded-full bg-orange py-3.5 text-sm font-bold text-white active:scale-95 disabled:opacity-50">
                        {beforeItems.length < (reqs?.min_before ?? 2) ? `ADD ${(reqs?.min_before ?? 2) - beforeItems.length} MORE BEFORE PHOTO(S) TO START` : "START WORK"}
                    </button>
                )}
                {next && (
                    <button data-testid="pro-next-status-btn" disabled={busy || afterShort > 0 || !!billBlock} onClick={() => setStatus(next.status)} className="w-full rounded-full bg-orange py-3.5 text-sm font-bold text-white active:scale-95 disabled:opacity-50">{afterShort > 0 ? `ADD ${afterShort} MORE AFTER PHOTO(S) TO FINISH` : billBlock || next.label}</button>
                )}
                {b.status === "INSPECTION" && (
                    <button data-testid="pro-submit-estimate-btn" onClick={() => setShowEst(true)} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-full bg-navy py-3.5 text-sm font-bold text-white active:scale-95"><FileText size={16} /> SUBMIT ESTIMATE</button>
                )}
                {b.status === "ESTIMATE APPROVAL REQUIRED" && <p className="text-center text-sm font-semibold text-orange-dark">Waiting for customer to approve the estimate…</p>}
                {b.status === "CUSTOMER REVIEW" && <p className="text-center text-sm font-semibold text-orange-dark">Waiting for customer to review and approve your work…</p>}
                {b.status === "WORK DONE - PAYMENT PENDING" && <p className="text-center text-sm font-semibold text-emerald-600">Customer approved — awaiting payment & HOMIIE confirmation.</p>}
                {["COMPLETED", "PAYMENT PENDING", "PAID", "CLOSED"].includes(b.status) && <p className="text-center text-sm font-semibold text-emerald-600">Job completed. HOMIIE will settle your payout.</p>}
            </div>

            {showEst && (
                <div className="fixed inset-0 z-50 flex items-end bg-navy/40" onClick={() => setShowEst(false)}>
                    <div className="mx-auto w-full max-w-md animate-fade-up rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-heading text-lg font-extrabold text-navy">Submit estimate</h3>
                        <p className="text-sm text-navy/55">Customer must approve before paid work begins.</p>
                        <div className="mt-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <label className="text-sm font-semibold text-navy">Visit charge <span className="text-[10px] font-semibold text-navy/40">(fixed by HOMIIE)</span></label>
                                <span data-testid="est-visit_charge" className="w-28 rounded-xl bg-navy/5 px-3 py-2 text-right font-semibold text-navy/70">{rupee(lockedVisit)}</span>
                            </div>
                            {[["labour", "Labour"], ["materials", "Materials"], ["other", "Convenience Fee"]].map(([k, label]) => (
                                <div key={k} className="flex items-center justify-between gap-3">
                                    <label className="text-sm font-semibold text-navy">{label}</label>
                                    <div className="flex items-center gap-1">
                                        <span className="text-navy/50">₹</span>
                                        <input data-testid={`est-${k}`} type="number" value={est[k]} onChange={(e) => setEst({ ...est, [k]: e.target.value })} className="w-28 rounded-xl border border-navy/15 px-3 py-2 text-right text-navy outline-none focus:border-orange" />
                                    </div>
                                </div>
                            ))}
                            <div className="flex items-center justify-between gap-3">
                                <label className="text-sm font-semibold text-navy">Platform fee <span className="text-[10px] font-semibold text-navy/40">(fixed)</span></label>
                                <span data-testid="est-platform-fee" className="w-28 rounded-xl bg-navy/5 px-3 py-2 text-right font-semibold text-navy/70">{rupee(PLATFORM_FEE)}</span>
                            </div>
                            <div className="flex justify-between border-t border-navy/10 pt-3 font-heading text-lg font-extrabold text-navy"><span>Total</span><span data-testid="est-total">{rupee(total)}</span></div>
                            {vfPaid > 0 && (
                                <>
                                    <div className="flex justify-between text-sm text-emerald-700"><span>Less: visit fee already paid</span><span>−{rupee(vfPaid)}</span></div>
                                    <div className="flex justify-between text-sm font-bold text-navy"><span>Customer pays now</span><span data-testid="est-amount-due">{rupee(Math.max(0, total - vfPaid))}</span></div>
                                </>
                            )}
                            <p className="text-[11px] text-navy/45">Visit charge and discounts are set by HOMIIE and can't be changed here.</p>
                        </div>
                        <button data-testid="est-submit-btn" disabled={busy} onClick={submitEstimate} className="mt-4 w-full rounded-full bg-orange py-3.5 text-sm font-bold text-white disabled:opacity-60">SEND FOR APPROVAL</button>
                    </div>
                </div>
            )}
            {capture && (
                <EvidenceCapture bookingId={id} bookingRef={b.booking_id} city={b.area_name} kind={capture.kind}
                    onCaptured={load} onClose={() => { setCapture(null); load(); }} />
            )}
        </ProShell>
    );
}

function EvidenceGroup({ kind, items, need, readOnly, onAdd, onDelete }) {
    const label = kind === "before" ? "Before" : "After";
    const enough = need == null || items.length >= need;
    return (
        <div data-testid={`evidence-group-${kind}`}>
            <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-bold text-navy">{label} photos</span>
                <span className={`text-xs font-semibold ${enough ? "text-emerald-600" : "text-amber-600"}`} data-testid={`evidence-count-${kind}`}>{items.length}{need != null ? `/${need}` : ""} {enough ? "✓" : ""}</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {items.map((e) => (
                    <div key={e.id} className="relative">
                        <img src={`${API}/files/${e.path}?auth=${getToken()}`} alt={label} className="h-20 w-20 rounded-lg border border-navy/10 object-cover" />
                        <span className={`absolute bottom-0 left-0 right-0 rounded-b-lg px-1 py-0.5 text-[8px] font-bold text-white ${e.location_verified ? "bg-emerald-600/80" : "bg-amber-600/80"}`}>{e.location_verified ? "GPS ✓" : "No GPS"}</span>
                        {!readOnly && <button data-testid={`evidence-del-${e.id}`} onClick={() => onDelete(e.id)} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-0.5 text-white"><Trash2 size={11} /></button>}
                    </div>
                ))}
                {!readOnly && <button data-testid={`evidence-add-${kind}`} onClick={onAdd} className="flex h-20 w-20 flex-col items-center justify-center rounded-lg border border-dashed border-navy/25 text-navy/40"><Camera size={20} /><span className="mt-0.5 text-[10px] font-semibold">{label}</span></button>}
            </div>
        </div>
    );
}

function Card({ children }) {
    return <div className="rounded-2xl border border-navy/10 bg-white p-4 shadow-[0_4px_16px_rgba(16,42,67,0.04)]">{children}</div>;
}
